import { type TripPackageLoaderResult } from '../TripPackageLoader';
import FlightPackage from './FlightPackage';
import {
    type FlightPackageDto,
    type FlightsMetaDto,
    type GetFlightsResponseDto,
} from '@clients/FlightApi';

export default class FlightPackageLoaderResultFactory {
    private readonly _adultCount: number;
    private readonly _childCount: number;
    private readonly _infantCount: number;
    private readonly _apiResult: GetFlightsResponseDto;
    private readonly _isCapacityTrackCampaign: boolean;
    private readonly _isForBundle: boolean;
    constructor({
        apiResult,
        adultCount,
        childCount,
        infantCount,
        isCapacityTrackCampaign = false,
        isForBundle = false,
    }: {
        apiResult: GetFlightsResponseDto;
        adultCount: number;
        childCount: number;
        infantCount: number;
        isCapacityTrackCampaign?: boolean;
        isForBundle?: boolean;
    }) {
        this._adultCount = adultCount;
        this._childCount = childCount;
        this._infantCount = infantCount;
        this._apiResult = apiResult;
        this._isCapacityTrackCampaign = isCapacityTrackCampaign;
        this._isForBundle = isForBundle;
    }

    private _getFlightPackage(
        flight: FlightPackageDto,
        meta: FlightsMetaDto,
        defaultPriceIndex: number,
        uniqueId?: string
    ) {
        const pkg = new FlightPackage(
            flight,
            {
                adultCount: this._adultCount,
                childCount: this._childCount,
                infantCount: this._infantCount,
            },
            meta,
            defaultPriceIndex,
            this._isCapacityTrackCampaign,
            uniqueId
        );
        pkg.isForBundlePackage = this._isForBundle;
        return pkg;
    }

    get list(): FlightPackage[] {
        return (this._apiResult.Flights ?? []).reduce((list, flight, index) => {
            if (flight.Prices?.length) {
                list = [
                    ...list,
                    ...flight.Prices.map(p =>
                        this._getFlightPackage(
                            flight,
                            this._apiResult.Meta ?? {},
                            flight.Prices?.indexOf(p) ?? 0
                        )
                    ),
                ];
                if (flight.MustAppearInAutoReserve) {
                    list.push(
                        this._getFlightPackage(
                            {
                                ...flight,
                                Prices: [],
                            },
                            this._apiResult.Meta ?? {},
                            0
                        )
                    );
                }
            } else {
                list.push(
                    this._getFlightPackage(
                        flight,
                        this._apiResult.Meta ?? {},
                        0,
                        `none-price-${index}`
                    )
                );
            }
            return list;
        }, [] as FlightPackage[]);
    }

    get loaderResult(): TripPackageLoaderResult<FlightPackage> {
        const routeInfo = this._apiResult.Meta?.RoutesInfo?.[0];
        return {
            list: this.list,
            originLocation: {
                latitude: routeInfo?.Origin?.Latitude || 0,
                longitude: routeInfo?.Origin?.Longitude || 0,
                title: routeInfo?.Origin?.PersianTitle,
            },
            destinationLocation: {
                latitude: routeInfo?.Destination?.Latitude || 0,
                longitude: routeInfo?.Destination?.Longitude || 0,
                title: routeInfo?.Origin?.PersianTitle,
            },
            filterMetaData: this._apiResult.Meta!,
            metaData: {
                routeNotFound: !!this._apiResult.Meta?.RouteNotFound,
                priceTrackEnabled: true,
            },
        };
    }
}
